import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { CaseTableRow } from '@/pages/ans/CasesPage';
import { fetchAnsEntity } from '@/services/ans';

/**
 * One operator, by CNPJ: its registrations (one CNPJ can hold two registros
 * ANS), its special regimes (an instrument, never a sanction), its penalties
 * with their board review and collection state, and the NIP demands against
 * it, counted and never listed per consumer.
 *
 * Every `Answer` is record-scoped: a refusal means THIS operator lacks the
 * substrate (no penalty in the snapshot, no NIP demand), never that ANS
 * cannot support the analysis.
 */
export function AnsEntityProfilePage() {
  const { cnpj = '' } = useParams();
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'entity', cnpj],
    queryFn: () => fetchAnsEntity(cnpj),
    enabled: cnpj.length > 0,
    // A 404 is an answer (no such CNPJ in any ANS registry), not a transient failure.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-6">
        <ErrorMessage
          message={notFound ? t('ans:entity.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <p className="text-xs text-muted-foreground">
          <Link to="/ans/entities" className="text-primary underline-offset-2 hover:underline">
            {t('ans:entity.back')}
          </Link>
        </p>
        <h1 className="text-lg font-semibold text-foreground">{data.entity.name}</h1>
        <p className="text-sm tabular-nums text-muted-foreground">
          {t('ans:entity.cnpj', { cnpj: data.entity.cnpj })}
          {data.entity.cnpj_valid === false ? ` · ${t('ans:entity.cnpjInvalid')}` : ''}
        </p>
      </header>

      <ExplainerCard i18nKey="ans:entity.explainer" />

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-registrations>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ans:entity.registrations.title')}
          </h2>
          {data.registrations.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('ans:entity.registrations.none')}</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.registrations.map((reg) => (
                <li
                  key={reg.registro}
                  className="rounded-md border bg-muted/30 p-2"
                  data-registro={reg.registro}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium tabular-nums text-foreground">{reg.registro}</span>
                    <span className="text-xs text-muted-foreground">
                      {t(`ans:entities.registryStatus.${reg.registry_status}`, {
                        defaultValue: reg.registry_status,
                      })}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[reg.modalidade, reg.cidade, reg.uf].filter(Boolean).join(' · ') || '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('ans:entity.registrations.registered', {
                      date: reg.registration_date ?? '—',
                    })}
                    {reg.deregistration_date
                      ? ` · ${t('ans:entity.registrations.deregistered', {
                          date: reg.deregistration_date,
                          reason: reg.deregistration_reason ?? '—',
                        })}`
                      : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-lg border bg-card p-4" data-regimes>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ans:entity.regimes.title')}
          </h2>
          <p className="mb-2 text-xs text-muted-foreground">{t('ans:entity.regimes.help')}</p>
          {data.regimes.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('ans:entity.regimes.none')}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {data.regimes.map((regime, index) => (
                <li
                  key={`${regime.operational_resolution ?? index}-${regime.publication_date ?? index}`}
                  className="border-b py-1"
                >
                  <span className="tabular-nums text-foreground">
                    {regime.publication_date ?? regime.year ?? '—'}
                  </span>
                  <span className="ml-2 text-muted-foreground">
                    {t('ans:entity.regimes.resolution', {
                      resolution: regime.operational_resolution ?? '—',
                    })}
                    {regime.uf ? ` · ${regime.uf}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="rounded-lg border bg-card p-4" data-history>
        <h2 className="mb-2 text-sm font-semibold text-foreground">
          {t('ans:entity.history.title')}
        </h2>
        <Answer answer={data.history} scope="record" help={t('ans:entity.history.help')}>
          {(h) => (
            <div className="space-y-2">
              <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                {(
                  [
                    ['cases', number.format(h.cases)],
                    [
                      'reviewed',
                      `${number.format(h.board_reviewed)} (${percent.format(h.reviewed_share)})`,
                    ],
                    ['reduced', number.format(h.reduced)],
                    ['maintained', number.format(h.maintained)],
                    ['originalTotal', currency.format(h.original_total)],
                    ['finalTotal', currency.format(h.final_total)],
                    ['years', h.first_year && h.last_year ? `${h.first_year}–${h.last_year}` : '—'],
                  ] as const
                ).map(([key, value]) => (
                  <div
                    key={key}
                    className="rounded-md border bg-muted/30 p-2"
                    data-history-field={key}
                  >
                    <dt className="text-xs text-muted-foreground">
                      {t(`ans:entity.history.${key}`)}
                    </dt>
                    <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-xs text-muted-foreground">
                {Object.entries(h.by_track)
                  .map(([track, n]) => `${track}: ${number.format(n)}`)
                  .join(' · ')}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ans:entity.guide.history" />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-collection>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ans:entity.collection.title')}
          </h2>
          <Answer answer={data.collection} scope="record" help={t('ans:entity.collection.help')}>
            {(k) => (
              <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                {(
                  [
                    ['paid', number.format(k.paid)],
                    ['pending', number.format(k.pending)],
                    ['inInstalments', number.format(k.in_instalments)],
                    ['suspended', number.format(k.suspended)],
                    ['dividaAtiva', number.format(k.divida_ativa)],
                    ['judiciallySuspended', number.format(k.judicially_suspended)],
                    ['finalTotal', currency.format(k.final_total)],
                    ['paidTotal', currency.format(k.paid_total)],
                  ] as const
                ).map(([key, value]) => (
                  <div
                    key={key}
                    className="rounded-md border bg-muted/30 p-2"
                    data-collection-field={key}
                  >
                    <dt className="text-xs text-muted-foreground">
                      {t(`ans:entity.collection.${key}`)}
                    </dt>
                    <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-nip>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ans:entity.nip.title')}
          </h2>
          <Answer answer={data.nip} scope="record" help={t('ans:entity.nip.help')}>
            {(n) => (
              <div className="space-y-2">
                <dl className="grid grid-cols-3 gap-2 text-sm">
                  {(
                    [
                      ['demands', number.format(n.demands)],
                      ['escalated', number.format(n.escalated_to_auto)],
                      ['withProcess', number.format(n.with_process)],
                    ] as const
                  ).map(([key, value]) => (
                    <div
                      key={key}
                      className="rounded-md border bg-muted/30 p-2"
                      data-nip-field={key}
                    >
                      <dt className="text-xs text-muted-foreground">
                        {t(`ans:entity.nip.${key}`)}
                      </dt>
                      <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-muted-foreground">
                  {Object.entries(n.by_year)
                    .map(([year, count]) => `${year}: ${number.format(count)}`)
                    .join(' · ')}
                </p>
              </div>
            )}
          </Answer>
        </section>
      </div>

      <section className="rounded-lg border bg-card" data-cases>
        <div className="flex items-center justify-between px-4 py-2">
          <h2 className="text-sm font-semibold text-foreground">
            {t('ans:entity.cases.title', {
              n: number.format(data.cases.length),
            })}
          </h2>
          <Link
            to={`/ans/cases?entity=${data.entity.cnpj}`}
            className="text-xs text-primary underline-offset-2 hover:underline"
          >
            {t('ans:entity.cases.openList')}
          </Link>
        </div>
        {data.cases.length === 0 ? (
          <p className="px-4 pb-3 text-xs text-muted-foreground">{t('ans:entity.cases.none')}</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.demand')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.operator')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.track')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.firstDecision')}</th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.boardOutcome')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ans:cases.columns.original')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ans:cases.columns.final')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('ans:cases.columns.creditStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {data.cases.map((row) => (
                  <CaseTableRow key={row.notice_number} row={row} currency={currency} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
